import { getIsIntakeFormV2 } from '@common/utils/intakeFormVersion';
import { Box, Stack } from '@mui/material';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import CustomTabPanel from '@/components/CustomTabPanel';
import { useProspectDetails, useProspectIntakeForm } from '@/queries/prospectDetails';
import BillingTab from './BillingTab';
import ClientDocuments from './ClientDocuments';
import EmailTimeline from './EmailTimeline';
import IdVerification from './IdVerification';
import IntakeResponseInformation from './IntakeResponseInformation/IntakeResponseInformation';
import IntakeResponseInformationV2 from './IntakeResponseInformation/IntakeResponseInformationV2';
import ProspectDetailsErrorPage from './ProspectDetailsErrorPage';
import ProspectDetailsSkeleton from './ProspectDetailsSkeleton';
import ProspectHeader from './ProspectHeader';
import ProspectQuoteFromResponse from './ProspectQuoteFromResponse';
import ProspectSummary from './ProspectSummary/ProspectSummary';
import ProspectSummaryIntakeOnly from './ProspectSummary/ProspectSummaryIntakeOnly';

const TAB_ROUTES = [
    'summary',
    'quote-from-response',
    'intake-from-response',
    'email-timeline',
    'client-documents',
    'id-verification',
    'client-invoice',
] as const;

function tabIndexFromRoute(route?: string): number {
    if (!route) return 0;
    const index = TAB_ROUTES.indexOf(route as (typeof TAB_ROUTES)[number]);
    return index === -1 ? 0 : index;
}

export default function ProspectDetails() {
    const { prospectId, prospectTabs } = useParams();

    const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null);
    const {
        data: prospect,
        isPending,
        isError,
        isFetching,
        refetch,
    } = useProspectDetails(prospectId, selectedVersionId);
    const { data: intakeForm } = useProspectIntakeForm(prospectId);
    const hasDraftIntakeForm = intakeForm?.status === 200 && intakeForm?.data?.isDraft === true;
    const activeVersionId =
        selectedVersionId ?? prospect?.prospectVersions?.[0]?.prospectVersionId ?? null;
    const currentTab = useMemo(() => tabIndexFromRoute(prospectTabs), [prospectTabs]);

    if (isError) {
        return (
            <ProspectDetailsErrorPage
                message="There was an error fetching prospect details"
                onRetry={refetch}
                isRetrying={isFetching}
            />
        );
    }

    if (isPending || !prospect) {
        return <ProspectDetailsSkeleton />;
    }

    const isIntakeFormV2 = getIsIntakeFormV2(prospect.practiceArea, prospect.matterType);

    return (
        <Box>
            <Stack>
                <ProspectHeader
                    prospectId={prospectId}
                    prospectTabs={prospectTabs}
                    prospect={prospect}
                    selectedVersionId={activeVersionId}
                    onSelectedVersionIdChange={setSelectedVersionId}
                    isVersionFetching={isFetching}
                />
                <CustomTabPanel value={currentTab} index={0}>
                    {prospect.templateType === 'IntakeOnly' ? (
                        <ProspectSummaryIntakeOnly
                            prospectId={prospectId}
                            prospect={prospect}
                            selectedVersionId={selectedVersionId}
                        />
                    ) : (
                        <ProspectSummary
                            prospectId={prospectId}
                            prospect={prospect}
                            selectedVersionId={selectedVersionId}
                        />
                    )}
                </CustomTabPanel>
                <CustomTabPanel value={currentTab} index={1}>
                    <ProspectQuoteFromResponse
                        prospectId={prospectId}
                        matterType={prospect.matterType}
                    />
                </CustomTabPanel>
                <CustomTabPanel value={currentTab} index={2}>
                    {prospect.practiceArea === 'RealEstate' && !isIntakeFormV2 ? (
                        <IntakeResponseInformation
                            prospectId={prospectId}
                            prospect={prospect}
                            selectedVersionId={activeVersionId}
                        />
                    ) : (
                        <IntakeResponseInformationV2
                            prospectId={prospectId}
                            prospect={prospect}
                            selectedVersionId={activeVersionId}
                        />
                    )}
                </CustomTabPanel>
                <CustomTabPanel value={currentTab} index={3}>
                    <EmailTimeline prospectId={prospectId} />
                </CustomTabPanel>
                <CustomTabPanel value={currentTab} index={4}>
                    <ClientDocuments
                        prospectId={prospectId}
                        selectedVersionId={activeVersionId}
                        isDraftPreview={hasDraftIntakeForm}
                    />
                </CustomTabPanel>
                <CustomTabPanel value={currentTab} index={5}>
                    <IdVerification prospectId={prospectId} prospect={prospect} />
                </CustomTabPanel>
                <CustomTabPanel value={currentTab} index={6}>
                    <BillingTab
                        prospectId={prospectId}
                        lawFirmId={prospect.lawFirmId}
                        lawFirmCompanyName={prospect.lawFirm?.companyName}
                        prospectName={prospect.prospectName}
                        completedAt={prospect.completedAt}
                    />
                </CustomTabPanel>
            </Stack>
        </Box>
    );
}
